/**
 * API empresa cliente — portal solo lectura (Fase 10).
 */

import api, { download, downloadPost, get } from './api'
import { MESSAGES, PAGINATION_CONFIG } from '../utils/constants'

function stripEmpty(params) {
  const out = { ...params }
  Object.keys(out).forEach((key) => {
    if (out[key] === '' || out[key] === null || out[key] === undefined) delete out[key]
  })
  return out
}

export const empresaClienteService = {
  DOCUMENTOS_MI_EMPRESA: [
    { key: 'nit', label: 'NIT' },
    { key: 'roe', label: 'ROE' },
    { key: 'matricula_comercio', label: 'MATRICULA DE COMERCIO' },
    { key: 'licencia_funcionamiento', label: 'LICENCIA DE FUNCIONAMIENTO' },
    { key: 'certificado_patronal_caja', label: 'CERTIFICADO PARTERNAL DE LA CAJA' },
    { key: 'formulario_inscripcion_gestora', label: 'FORMULARIO DE INSCRIPCION DE GESTORA' },
    { key: 'certificacion_nit', label: 'CERTIFICACION DE NIT' },
  ],

  async getDashboard() {
    try {
      const response = await get('/empresa-cliente/dashboard')
      if (response.data.success) {
        return { success: true, data: response.data.data, message: response.data.message }
      }
      return { success: false, message: response.data.message || MESSAGES.ERROR_FETCH }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || MESSAGES.ERROR_FETCH }
    }
  },

  async listAlertas(params = {}) {
    try {
      const queryParams = stripEmpty({
        page: params.page || 1,
        per_page: params.per_page || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
        resuelta: params.resuelta,
        leida: params.leida,
      })
      const response = await get('/empresa-cliente/alertas', queryParams)
      if (response.data.success) {
        return { success: true, data: response.data.data, message: response.data.message }
      }
      return { success: false, message: response.data.message || MESSAGES.ERROR_FETCH }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || MESSAGES.ERROR_FETCH }
    }
  },

  async getMiConsultora() {
    try {
      const response = await get('/empresa-cliente/mi-consultora')
      if (response.data.success) {
        return { success: true, data: response.data.data, message: response.data.message }
      }
      return { success: false, message: response.data.message || MESSAGES.ERROR_FETCH }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || MESSAGES.ERROR_FETCH }
    }
  },

  async listPersonal(params = {}) {
    try {
      const queryParams = stripEmpty({
        page: params.page || 1,
        per_page: params.per_page || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
        search: params.search || '',
        estado_modulo: params.estado_modulo || '',
      })
      const response = await get('/empresa-cliente/personal', queryParams)
      if (response.data.success) {
        return { success: true, data: response.data.data, message: response.data.message }
      }
      return { success: false, message: response.data.message || MESSAGES.ERROR_FETCH }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || MESSAGES.ERROR_FETCH }
    }
  },

  async listDeclaracionesMensuales() {
    try {
      const response = await get('/empresa-cliente/declaraciones-mensuales')
      if (response.data.success) {
        const raw = response.data.data
        return {
          success: true,
          data: { items: raw?.items ?? [] },
          message: response.data.message,
        }
      }
      return { success: false, message: response.data.message || MESSAGES.ERROR_FETCH, data: { items: [] } }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || MESSAGES.ERROR_FETCH,
        data: { items: [] },
      }
    }
  },

  async descargarDeclaracionMensual(id, nombreOriginal) {
    await download(
      `/empresa-cliente/declaraciones-mensuales/${id}/descargar`,
      {},
      nombreOriginal || `declaracion-${id}`
    )
  },

  async fetchDeclaracionMensualVistaPreviaBlob(id) {
    try {
      const response = await api.get(
        `/empresa-cliente/declaraciones-mensuales/${id}/vista-previa`,
        { responseType: 'blob' }
      )
      const blob = response.data
      const cd = response.headers['content-disposition'] || ''
      const m = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(cd)
      const nombre = m ? decodeURIComponent(m[1]) : `declaracion-${id}`
      const contentType =
        (blob instanceof Blob && blob.type && blob.type !== 'application/octet-stream'
          ? blob.type
          : null) ||
        response.headers['content-type'] ||
        'application/octet-stream'
      return { success: true, blob, nombreOriginal: nombre, contentType }
    } catch (error) {
      const d = error.response?.data
      if (d instanceof Blob) {
        try {
          const j = JSON.parse(await d.text())
          return { success: false, message: j.message || MESSAGES.ERROR_FETCH }
        } catch {
          /* noop */
        }
      }
      return {
        success: false,
        message: error.message || error.response?.data?.message || MESSAGES.ERROR_FETCH,
      }
    }
  },

  async descargarDeclaracionesMensualesZip(meses, fallbackName = 'declaraciones.zip') {
    try {
      await downloadPost(
        '/empresa-cliente/declaraciones-mensuales/descarga-zip',
        { meses },
        fallbackName
      )
      return { success: true }
    } catch (error) {
      const msg =
        (error instanceof Error && error.message) ||
        error.response?.data?.message ||
        MESSAGES.ERROR.SERVER_ERROR
      return { success: false, message: msg }
    }
  },

  async listMiEmpresaDocumentos() {
    try {
      const response = await get('/empresa-cliente/mi-empresa/documentos')
      if (response.data.success) {
        const raw = response.data.data
        return {
          success: true,
          data: { items: raw?.items ?? [] },
          message: response.data.message,
        }
      }
      return { success: false, message: response.data.message || MESSAGES.ERROR_FETCH, data: { items: [] } }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || MESSAGES.ERROR_FETCH,
        data: { items: [] },
      }
    }
  },

  async fetchMiEmpresaDocumentoVistaPreviaBlob(tipoDocumento) {
    try {
      const response = await api.get(`/empresa-cliente/mi-empresa/documentos/${tipoDocumento}/vista-previa`, {
        responseType: 'blob',
      })
      const blob = response.data
      const cd = response.headers['content-disposition'] || ''
      const m = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(cd)
      const nombre = m ? decodeURIComponent(m[1]) : `${tipoDocumento}.pdf`
      const contentType =
        (blob instanceof Blob && blob.type && blob.type !== 'application/octet-stream'
          ? blob.type
          : null) ||
        response.headers['content-type'] ||
        'application/octet-stream'
      return { success: true, blob, nombreOriginal: nombre, contentType }
    } catch (error) {
      const d = error.response?.data
      if (d instanceof Blob) {
        try {
          const j = JSON.parse(await d.text())
          return { success: false, message: j.message || MESSAGES.ERROR_FETCH }
        } catch {
          /* noop */
        }
      }
      return {
        success: false,
        message: error.message || error.response?.data?.message || MESSAGES.ERROR_FETCH,
      }
    }
  },

  async descargarMiEmpresaDocumento(tipoDocumento, nombreOriginal) {
    await download(
      `/empresa-cliente/mi-empresa/documentos/${tipoDocumento}/descargar`,
      {},
      nombreOriginal || `${tipoDocumento}.pdf`
    )
  },

  /** PDFs que la consultora/colaborador sube en «otros documentos» de la empresa. */
  async listOtrosDocumentosColaborador() {
    try {
      const response = await get('/empresa-cliente/otros-documentos')
      if (response.data.success) {
        const raw = response.data.data
        return {
          success: true,
          data: { items: raw?.items ?? [] },
          message: response.data.message,
        }
      }
      return { success: false, message: response.data.message || MESSAGES.ERROR_FETCH, data: { items: [] } }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || MESSAGES.ERROR_FETCH,
        data: { items: [] },
      }
    }
  },

  async fetchOtroDocumentoColaboradorVistaPreviaBlob(id) {
    try {
      const response = await api.get(`/empresa-cliente/otros-documentos/${id}/vista-previa`, {
        responseType: 'blob',
      })
      const blob = response.data
      if (blob instanceof Blob && blob.type?.includes('application/json')) {
        const text = await blob.text()
        try {
          const j = JSON.parse(text)
          return { success: false, message: j.message || MESSAGES.ERROR_FETCH }
        } catch {
          return { success: false, message: MESSAGES.ERROR_FETCH }
        }
      }
      return {
        success: true,
        blob,
        contentType: response.headers['content-type'] || blob.type || 'application/pdf',
      }
    } catch (error) {
      const d = error.response?.data
      if (d instanceof Blob) {
        try {
          const j = JSON.parse(await d.text())
          return { success: false, message: j.message || MESSAGES.ERROR_FETCH }
        } catch {
          /* noop */
        }
      }
      return {
        success: false,
        message: error.response?.data?.message || MESSAGES.ERROR_FETCH,
      }
    }
  },

  async descargarOtroDocumentoColaborador(id, nombreOriginal) {
    await download(
      `/empresa-cliente/otros-documentos/${id}/descargar`,
      {},
      nombreOriginal || `documento-${id}.pdf`
    )
  },
}
